<template>
  <section class="mailbox-statistics">
    <div class="title">访问统计</div>
    <div class="date-change-box">
      <el-radio-group v-model="rangeType" fill="#003d92" size="medium" @change="rangeChanged">
        <el-radio-button label="today">当天</el-radio-button>
        <el-radio-button label="7days">最近7天</el-radio-button>
        <el-radio-button label="30days">最近三十天</el-radio-button>
        <el-radio-button label="custom">自定义</el-radio-button>
      </el-radio-group>
      <el-date-picker
        v-if="rangeType === 'custom'"
        v-model="customRange"
        type="daterange"
        size="small"
        range-separator="至"
        start-placeholder="开始日期"
        end-placeholder="结束日期"
        value-format="timestamp"
        :picker-options="pickerOptions"
        @change="customRangeChanged"
      ></el-date-picker>
    </div>

    <div class="chart-panel line-panel" v-loading="loading">
      <div class="panel-title">邮箱类型趋势</div>
      <div ref="lineChart" class="chart"></div>
    </div>
    <div class="pie-grid">
      <div class="chart-panel" v-loading="loading">
        <div class="panel-title">邮箱类型占比</div>
        <div ref="typePie" class="chart"></div>
      </div>
      <div class="chart-panel" v-loading="loading">
        <div class="panel-title">调用结果占比</div>
        <div ref="requestPie" class="chart"></div>
      </div>
    </div>
  </section>
</template>

<script>
import moment from 'moment'
import { getMailboxStat } from '@/services/riskMailbox'

const colors = ['#4c78a8', '#2a9d8f', '#e9c46a', '#e76f51', '#8f6bb3', '#77818f', '#59a14f']

export default {
  name: 'MailboxCharts',
  data () {
    return {
      rangeType: 'today',
      customRange: [],
      loading: false,
      charts: [],
      pickerOptions: {
        disabledDate (date) {
          const today = moment().endOf('day')
          const earliest = moment().startOf('day').subtract(89, 'days')
          return moment(date).isAfter(today) || moment(date).isBefore(earliest)
        }
      }
    }
  },
  methods: {
    rangeParams (type) {
      const end = moment().startOf('day')
      let start = end.clone()
      if (type === '7days') start = end.clone().subtract(6, 'days')
      if (type === '30days') start = end.clone().subtract(29, 'days')
      return {
        start_date: start.unix(),
        end_date: end.unix()
      }
    },
    rangeChanged (type) {
      if (type === 'custom') return
      this.fetchData(this.rangeParams(type))
    },
    customRangeChanged (value) {
      if (!value || value.length !== 2) return
      this.fetchData({
        start_date: Math.floor(value[0] / 1000),
        end_date: Math.floor(value[1] / 1000)
      })
    },
    async fetchData (params) {
      this.loading = true
      try {
        const response = await getMailboxStat(params)
        this.drawCharts(response.data || {})
      } catch (error) {
        this.$message.error('邮箱访问统计加载失败')
        this.drawCharts({})
      } finally {
        this.loading = false
      }
    },
    chartInstance (element) {
      return this.$echarts.getInstanceByDom(element) || this.$echarts.init(element)
    },
    drawCharts (data) {
      const distribution = data.type_distribution || []
      const trend = data.type_trend || []
      const requests = data.request_statistic || []
      const legend = distribution.map(item => item.label)
      const hourly = data.granularity === 'hour'

      const line = this.chartInstance(this.$refs.lineChart)
      line.setOption({
        color: colors,
        tooltip: { trigger: 'axis' },
        legend: { data: legend, bottom: 0, type: 'scroll' },
        grid: { left: 40, right: 24, top: 28, bottom: 58, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: trend.map(item => moment(item.time).format(hourly ? 'HH:00' : 'MM-DD'))
        },
        yAxis: { type: 'value', minInterval: 1 },
        series: distribution.map((item, index) => ({
          name: item.label,
          type: 'line',
          smooth: true,
          symbolSize: 5,
          data: trend.map(point => {
            const value = (point.values || []).find(entry => entry.key === item.key)
            return value ? value.value : 0
          }),
          lineStyle: { width: 2 },
          itemStyle: { color: colors[index] }
        }))
      }, true)

      const typePie = this.chartInstance(this.$refs.typePie)
      typePie.setOption(this.pieOption(
        distribution.map(item => ({ name: item.label, value: item.value })),
        colors
      ), true)

      const requestPie = this.chartInstance(this.$refs.requestPie)
      requestPie.setOption(this.pieOption(
        requests.map(item => ({ name: item.label, value: item.value })),
        ['#2a9d8f', '#e76f51']
      ), true)

      this.charts = [line, typePie, requestPie]
    },
    pieOption (data, palette) {
      return {
        color: palette,
        tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
        legend: { bottom: 0, type: 'scroll' },
        series: [{
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['50%', '44%'],
          avoidLabelOverlap: true,
          label: { formatter: '{b}\n{d}%' },
          data
        }]
      }
    },
    resizeCharts () {
      this.charts.forEach(chart => chart.resize())
    }
  },
  mounted () {
    this.fetchData(this.rangeParams('today'))
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeCharts)
    this.charts.forEach(chart => chart.dispose())
  }
}
</script>

<style lang="scss" scoped>
.mailbox-statistics {
  margin-top: 40px;

  .title {
    margin-bottom: 15px;
    color: #70778d;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    letter-spacing: 0;
  }
}

.date-change-box {
  display: flex;
  align-items: center;
  margin-bottom: 32px;

  .el-radio-group {
    margin-right: 20px;
  }
}

.chart-panel {
  min-width: 0;
  background: #fff;
  box-shadow: 0 4px 8px rgba(17, 39, 98, 0.04);
  border-radius: 4px;
  padding: 18px 20px 12px;
  box-sizing: border-box;
}

.panel-title {
  height: 24px;
  font-size: 15px;
  font-weight: 500;
  color: #384057;
}

.chart {
  width: 100%;
  height: 300px;
}

.line-panel .chart {
  height: 330px;
}

.pie-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-top: 18px;
}

@media (max-width: 900px) {
  .date-change-box {
    align-items: flex-start;
    flex-direction: column;

    .el-radio-group {
      margin: 0 0 12px;
    }
  }

  .pie-grid {
    grid-template-columns: 1fr;
  }
}
</style>
